import { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { CaretIcon, CheckMark } from 'src/components/Icons/icons';
import './optionMenu.css';

/** Compact single-choice picker in the style of the calendar view picker. */
export const OptionMenu = <T extends string>({
    label,
    value,
    options,
    onChange,
    className,
}: {
    label: string;
    value: T;
    options: { value: T; label: string }[];
    onChange: (value: T) => void;
    className?: string;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const container = useRef<HTMLDivElement>(null);
    const trigger = useRef<HTMLButtonElement>(null);
    const selected = options.find(option => option.value === value);

    useEffect(() => {
        if (!isOpen) return;
        const closeOnOutsideClick = (event: MouseEvent) => {
            if (!container.current?.contains(event.target as Node))
                setIsOpen(false);
        };
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            setIsOpen(false);
            trigger.current?.focus();
        };
        document.addEventListener('mousedown', closeOnOutsideClick);
        document.addEventListener('keydown', closeOnEscape);
        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [isOpen]);

    return (
        <div ref={container} className={cx('mundu-option-menu', className)}>
            <button
                ref={trigger}
                type="button"
                className="mundu-option-menu-trigger"
                aria-label={`${label}: ${selected?.label ?? ''}`}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                onClick={() => setIsOpen(open => !open)}
            >
                <span>{selected?.label}</span>
                <span
                    className="relative block h-3 w-3 flex-shrink-0"
                    aria-hidden="true"
                >
                    <CaretIcon />
                </span>
            </button>
            {isOpen && (
                <div
                    className="mundu-option-menu-list"
                    role="menu"
                    aria-label={label}
                >
                    {options.map(option => {
                        const isSelected = option.value === value;
                        return (
                            <button
                                type="button"
                                role="menuitemradio"
                                aria-checked={isSelected}
                                key={option.value}
                                className={
                                    isSelected
                                        ? 'mundu-option-menu-active'
                                        : undefined
                                }
                                onClick={() => {
                                    onChange(option.value);
                                    setIsOpen(false);
                                }}
                            >
                                <span>{option.label}</span>
                                <span
                                    className="mundu-option-menu-check"
                                    aria-hidden="true"
                                >
                                    {isSelected ? <CheckMark /> : null}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
